import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { tagFromName } from '@kronos/domain'
import type { MachineKind } from '@kronos/domain'
import { HttpError, repository } from '../../data'
import { FieldLabel, Modal } from '../kit'
import { problemOf } from './format'

/**
 * Add a robot or an office computer. The tag follows the name until somebody
 * types their own — an existing office tag, say — and it is the one field
 * that cannot change later, because devices join carrying it.
 */
export function AddMachine({
  robots,
  taken,
  initial,
  onClose,
}: {
  robots: readonly { id: string; fleetNumber: string }[]
  taken: readonly string[]
  /** From a device already on the network: its name, its tag (fixed), a guess at its kind. */
  initial?: { name: string; tag: string; kind: MachineKind; deviceId: string; device: string; keeps: readonly string[]; added: boolean }
  onClose: () => void
}): JSX.Element {
  const queryClient = useQueryClient()
  const [name, setName] = useState(initial?.name ?? '')
  const [kind, setKind] = useState<MachineKind>(initial?.kind ?? 'robot')
  const [ownTag, setOwnTag] = useState<string | null>(initial?.tag ?? null)
  const [sshUser, setSshUser] = useState('nupeak')
  const [robotId, setRobotId] = useState('')
  const [problem, setProblem] = useState('')

  const tag = ownTag ?? tagFromName(name)
  const clash = taken.includes(tag)
  const ready = name.trim() !== '' && tag !== '' && !clash && sshUser.trim() !== ''

  const add = useMutation({
    mutationFn: () =>
      repository.addMachine({
        name: name.trim(),
        tag,
        kind,
        sshUser: sshUser.trim(),
        robotId: kind === 'robot' && robotId ? robotId : null,
        ...(initial ? { deviceId: initial.deviceId } : {}),
      }),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['connectivity'] })
      // Added, but the device could not be given its own tag: say why, and stay open.
      if (r.tagged) setProblem(`${name.trim()} was added, but ${r.tagged}`)
      else onClose()
    },
    onError: (e: Error) => {
      setConflict(e instanceof HttpError && e.code === 'base_grants_ssh')
      setProblem(problemOf(e))
    },
  })

  // The other network rules already give SSH to this tag (the R2 pilot rule):
  // take them out, through the same checked path as editing them by hand, then add.
  const [conflict, setConflict] = useState(false)
  const release = useMutation({
    mutationFn: () => repository.releaseTag(tag),
    onSuccess: () => {
      setConflict(false)
      setProblem('')
      add.mutate()
    },
    onError: (e: Error) => setProblem(problemOf(e)),
  })

  return (
    <Modal
      title="Add a machine"
      subtitle={initial ? 'It is already on the network. From now on Kronos decides who can reach it.' : 'Then join it to the network with the command on its card.'}
      onClose={onClose}
      footer={
        <button className="primary" disabled={!ready || add.isPending} onClick={() => add.mutate()}>
          Add
        </button>
      }
    >
      <div className="stack">
        <div className="segmented" role="tablist" aria-label="Kind">
          <button role="tab" aria-selected={kind === 'robot'} onClick={() => setKind('robot')}>
            Robot
          </button>
          <button role="tab" aria-selected={kind === 'computer'} onClick={() => setKind('computer')}>
            Office computer
          </button>
        </div>

        <label className="stack" style={{ gap: 4 }}>
          <span className="field-label">Name</span>
          <input value={name} placeholder={kind === 'robot' ? 'R2' : 'Office 3090'} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <FieldLabel
            label="Network tag"
            hint="How the network knows this machine. Fixed once a device has joined with it. Use an existing tag such as tag:office to manage computers that are already on the network."
          />
          <input className="cx-mono" value={tag} readOnly={!!initial} onChange={(e) => setOwnTag(e.currentTarget.value.trim().toLowerCase())} />
          {clash ? <span className="critical">Another machine already has {tag}.</span> : null}
          {initial?.added ? (
            <span className="muted cx-small">
              {initial.device} shares {initial.keeps.join(', ')} with other devices, so it gets its own tag. It keeps {initial.keeps.join(', ')} too, so the rules that name it still apply.
            </span>
          ) : null}
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <FieldLabel label="Login user" hint="The Linux account SSH logs into. Every robot uses nupeak." />
          <input className="cx-mono" value={sshUser} onChange={(e) => setSshUser(e.currentTarget.value.trim())} />
        </label>

        {kind === 'robot' && robots.length > 0 ? (
          <label className="stack" style={{ gap: 4 }}>
            <span className="field-label">Which robot in Kronos</span>
            <select value={robotId} onChange={(e) => setRobotId(e.currentTarget.value)}>
              <option value="">Not linked</option>
              {robots.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.fleetNumber}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {conflict ? (
          <div className="cx-answer">
            <strong>Something else already lets people SSH into {tag}</strong>
            <span>
              A rule in the other network rules — on R2 it is the pilot rule from before Kronos — gives SSH to this tag, so taking access away here
              would not take it away. Kronos can remove that rule and manage {name.trim() || 'it'} from now on.
            </span>
            <button className="primary" disabled={release.isPending || add.isPending} onClick={() => release.mutate()}>
              {release.isPending ? 'Removing…' : `Remove that rule and add ${name.trim() || 'it'}`}
            </button>
          </div>
        ) : problem ? (
          <p className="critical" role="alert">{problem}</p>
        ) : null}
      </div>
    </Modal>
  )
}
